import { useState } from "react";

import { Lock, Security } from "@assets";
import { ErrorComponent } from "@components/ErrorComponent";
import { Row } from "@components/Flex";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body, BodySmall } from "@components/Typography";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { Spinner } from "@entry/ui";
import { useBankLinkingFlow, useBankLinkToken } from "@hooks";
import { i18n, Translations } from "@i18n";
import { useStyles } from "./styles";
import { BankLinkItemProps, InsufficientBankBalanceRouteComponentProps } from "./types";

export const InsufficientBankBalanceRoute = () => {
  const { token, loading, error } = useBankLinkToken();

  if (loading) return <Loader />;
  if (error || !token) return <ErrorComponent />;

  return <InsufficientBankBalanceRouteComponent token={token} />;
};

const InsufficientBankBalanceRouteComponent = ({
  token,
}: InsufficientBankBalanceRouteComponentProps) => {
  const styles = useStyles();

  // Plaid Link renders as an overlay; hide our content underneath it so
  // only the shell background shows behind the modal.
  const [plaidOpen, setPlaidOpen] = useState(false);

  const { open, ready, isSendingToken } = useBankLinkingFlow({
    token,
    onPlaidClose: () => setPlaidOpen(false),
  });

  const handleLinkBank = () => {
    if (ready) {
      setPlaidOpen(true);
      open();
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.InsufficientBankBalanceRouteTitle),
    subtitle: i18n.t(Translations.InsufficientBankBalanceRouteSubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.InsufficientBankBalanceRoutePrimaryCTA),
        onClick: handleLinkBank,
        loading: isSendingToken,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      {plaidOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spinner style={{ width: 34, height: 34, borderWidth: 3 }} />
        </div>
      )}

      <div style={{ visibility: plaidOpen ? "hidden" : "visible" }}>
        <div style={styles.card}>
          <BankLinkItem
            icon={<Security />}
            title={i18n.t(Translations.InsufficientBankBalanceRouteItem1Title)}
            subtitle={i18n.t(Translations.InsufficientBankBalanceRouteItem1Subtitle)}
          />

          <Spacing size={SpacingSizes.M} />

          <BankLinkItem
            icon={<Lock />}
            title={i18n.t(Translations.InsufficientBankBalanceRouteItem2Title)}
            subtitle={i18n.t(Translations.InsufficientBankBalanceRouteItem2Subtitle)}
          />
        </div>
        <Spacing size={SpacingSizes.L} />
      </div>
    </OnboardingLayout>
  );
};

export const BankLinkItem = ({ icon, title, subtitle }: BankLinkItemProps) => {
  const styles = useStyles();

  return (
    <>
      <Row>
        {icon}
        <Body style={styles.itemTitle}>{title}</Body>
      </Row>
      <Spacing size={SpacingSizes.XS} />
      <BodySmall style={styles.secondaryText}>{subtitle}</BodySmall>
    </>
  );
};
